<div class="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pb-4">

    <div class="flex flex-wrap items-end gap-3.5">
        <div>
            <h3 class="m-0 font-heading text-2xl font-bold tracking-[-0.02em] text-foreground">
                Content that raised a red flag
            </h3>
            <p class="m-0 mt-1.5 max-w-[64ch] text-[13px] leading-relaxed text-muted-foreground">{{ headline() }}</p>
        </div>
    </div>

    @if (loading()) {
    <p class="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground" aria-busy="true">
        Loading screening results…
    </p>
    } @else if (showFlaggedPosts()) {
    @if (results.briefId(); as briefId) {
    <ul class="m-0 flex list-none flex-col gap-3 p-0">
        @for (post of flaggedPosts(); track post.noodleCreatorId + post.item.id) {
        <li>
            <scr-flagged-content-card [post]="post" [briefId]="briefId" />
        </li>
        }
    </ul>
    }
    } @else if (detailUnavailable()) {
    <scr-coming-soon icon="flag" heading="Flag detail is on its way" [body]="detailUnavailableBody()" />
    } @else if (nothingFlagged()) {
    <p class="flex items-center justify-center gap-2.5 rounded-xl border border-dashed border-border p-10 text-sm text-muted-foreground">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
            <path d="m9 12 2 2 4-4" />
        </svg>
        No content has raised a red flag.
    </p>
    } @else {

    <div class="flex flex-wrap gap-2">
        @for (chip of filterChips(); track chip.id) {
        <button type="button" (click)="setFilter(chip.id)" [attr.aria-pressed]="filter() === chip.id"
            class="rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors"
            [class]="filter() === chip.id ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground/80 hover:border-foreground/30'">
            {{ chip.label }}
        </button>
        }
    </div>

    @if (visibleFlags().length) {
    <ul class="m-0 flex list-none flex-col gap-3 p-0">
        @for (flag of visibleFlags(); track flag.id) {
        <li class="grid gap-4 rounded-xl border border-border bg-card p-4 shadow-sm md:grid-cols-[1fr_178px]"
            [class]="flag.severity === 'high' ? 'border-l-[3px] border-l-red-600' : flag.severity === 'medium' ? 'border-l-[3px] border-l-amber-500' : flag.severity === 'low' ? 'border-l-[3px] border-l-yellow-500' : 'border-l-[3px] border-l-border'">

            <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2.5">
                    <span class="font-heading text-base font-bold text-foreground">{{ creatorName(flag) }}</span>
                    <scr-severity-pill [severity]="flag.severity" />
                    <span class="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                        [class]="flag.kind === 'missing' ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'">
                        {{ flag.kind === 'missing' ? 'Missing requirement' : 'Rule breach' }}
                    </span>
                </div>

                @if (flag.statement) {
                <div class="mt-3 flex gap-3 rounded-md bg-muted p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mt-0.5 shrink-0 text-red-500 dark:text-red-400" aria-hidden="true">
                        <path d="m7.5 4.27 9 5.15" />
                        <circle cx="12" cy="12" r="10" />
                        <path d="m4.9 4.9 14.2 14.2" />
                    </svg>
                    <div class="min-w-0">
                        <p class="m-0 text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">{{
                            ruleCaption(flag) }}</p>
                        <p class="m-0 mt-1 text-sm leading-relaxed text-foreground">{{ flag.statement }}</p>
                    </div>
                </div>
                }

                @if (flag.finding) {
                <p class="m-0 mt-3 text-[13.5px] leading-relaxed text-foreground/80">{{ flag.finding }}</p>
                }

                @if (flag.quote) {
                <blockquote class="m-0 mt-3 border-l-2 border-brand-studio pl-3 text-[14.5px] italic leading-relaxed text-foreground/80">
                    “{{ flag.quote }}”
                </blockquote>
                }

                @if (flag.at) {
                <p class="m-0 mt-3 text-[11.5px] text-muted-foreground">
                    Timestamp <b class="font-semibold text-foreground/80">{{ flag.at }}</b>
                </p>
                }
            </div>

            <div class="flex shrink-0 flex-col gap-2">
                @if (flag.contentUrl) {
                <a [href]="flag.contentUrl" target="_blank" rel="noopener noreferrer"
                    class="secondary-button inline-flex w-full items-center justify-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
                    </svg>
                    Watch<span class="sr-only"> — opens in a new tab</span>
                </a>
                } @else {
                <button type="button" [attr.aria-disabled]="true"
                    class="secondary-button inline-flex w-full cursor-not-allowed items-center justify-center gap-2 opacity-60">
                    Watch
                    <span class="sr-only">— we don't have a link to this post yet</span>
                </button>
                }

                <button type="button" [attr.aria-disabled]="true"
                    class="secondary-button inline-flex w-full cursor-not-allowed items-center justify-center gap-1.5 opacity-60">
                    Reject
                    <span class="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Soon</span>
                    <span class="sr-only">— flag decisions are coming soon</span>
                </button>
                <button type="button" [attr.aria-disabled]="true"
                    class="inline-flex w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-muted-foreground opacity-60">
                    Override
                    <span class="sr-only">— flag decisions are coming soon</span>
                </button>
            </div>
        </li>
        }
    </ul>
    } @else {
    <p class="flex items-center justify-center gap-2.5 rounded-xl border border-dashed border-border p-10 text-sm text-muted-foreground">
        No flagged content matches this filter.
    </p>
    }
    }
</div>
